// Experiments: saved tests (Acuity E or Contrast E) with their look and method. Programs
// run them in order. Each experiment is a JSON document (docs/EXPERIMENT_FORMAT.md) that can
// be downloaded and imported; editing makes a new version, and programs keep the version
// they were saved with until they are brought up to date here.

import { useState } from "react";
import { api, type Experiment, type ImportCheck } from "../api";
import { useAuth } from "../auth";
import { completeExperiment, ExperimentFields } from "../components/ExperimentFields";
import { errorText, useLoad } from "../hooks";
import { defaultExperiment, type ExperimentConfig, type TestKind } from "../lib/protocol";
import { experimentSummary } from "../lib/summaries";

type Editing = { experiment: Experiment | null; start: ExperimentConfig; name: string; description: string };

export function ExperimentsPage() {
  const { user } = useAuth();
  const list = useLoad(() => api.experiments(), []);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [outdated, setOutdated] = useState<Experiment | null>(null);
  const canEdit = user?.role === "admin" || user?.role === "researcher";

  const startNew = (kind: TestKind) => setEditing({ experiment: null, start: defaultExperiment(kind), name: "", description: "" });
  const remove = async (e: Experiment) => {
    try { await api.deleteExperiment(e.id); list.reload(); } catch (err) { setError(errorText(err)); }
  };

  return (
    <>
      <h1>Experiments</h1>
      <p className="muted">An experiment is one test (acuity or contrast) with its own look and method. Programs run
        experiments in order, each as many times as you choose. Hover the <span className="help-dot static">i</span> next to
        any setting for what it does, with a drawing, the maths and suggested values.</p>
      {error && <div className="error">{error}</div>}
      <div className="card">
        <table>
          <thead><tr><th>Experiment</th><th>Test</th><th>What it does</th><th>Version</th><th>Used by</th><th /></tr></thead>
          <tbody>
            {(list.data ?? []).map((e) => (
              <tr key={e.id} className={e.active ? "" : "muted"}>
                <td><strong>{e.name}</strong>{!e.active && <> <span className="badge">off</span></>}
                  <div className="small muted">{e.description}</div></td>
                <td>{e.kind === "acuity" ? "Acuity E" : "Contrast E"}</td>
                <td className="small">{experimentSummary(e.config)}</td>
                <td>{e.version}</td>
                <td className="small">{e.used_by.map((p) => `${p.name}${p.versions.some((v) => v < e.version) ? " (older version)" : ""}`).join(", ") || "—"}</td>
                <td className="small" style={{ whiteSpace: "nowrap" }}>
                  {canEdit && <button className="link" onClick={() => setEditing({ experiment: e, start: e.config, name: e.name, description: e.description })}>Edit</button>}
                  {canEdit && <> · <button className="link" onClick={() => setEditing({ experiment: null, start: e.config, name: `${e.name} (copy)`, description: e.description })}>Copy</button></>}
                  {" · "}<a href={api.experimentFileUrl(e.id)} download>Download</a>
                  {user?.role === "admin" && e.used_by.length === 0 && <> · <button className="link" onClick={() => void remove(e)}>Delete</button></>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {canEdit && editing === null && (
          <div className="row" style={{ marginTop: "0.8rem" }}>
            <button onClick={() => startNew("acuity")}>New acuity experiment</button>
            <button onClick={() => startNew("contrast")}>New contrast experiment</button>
          </div>
        )}
      </div>
      {outdated && <UpdatePrograms e={outdated} onDone={() => { setOutdated(null); list.reload(); }} />}
      {editing && (
        <ExperimentEditor key={`${editing.experiment?.id ?? "new"}-${editing.name}`} editing={editing}
          onDone={(saved) => {
            setEditing(null);
            list.reload();
            if (saved && saved.used_by.some((p) => p.versions.some((v) => v < saved.version))) setOutdated(saved);
          }} />
      )}
      {canEdit && <ImportCard onImported={list.reload} />}
    </>
  );
}

function ExperimentEditor({ editing, onDone }: { editing: Editing; onDone: (saved: Experiment | null) => void }) {
  const [name, setName] = useState(editing.name);
  const [description, setDescription] = useState(editing.description);
  const [active, setActive] = useState(editing.experiment?.active ?? true);
  const [cfg, setCfg] = useState<ExperimentConfig>(completeExperiment(structuredClone(editing.start)));
  const [json, setJson] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const e = editing.experiment;
  const save = async () => {
    setError(null);
    try {
      const body = { name: name.trim(), description, config: cfg, active };
      onDone(e ? await api.updateExperiment(e.id, body) : await api.createExperiment(body));
    } catch (err) {
      setError(errorText(err));
    }
  };
  return (
    <div className="card experiment-editor">
      <h2 style={{ marginTop: 0 }}>{e ? `Edit ${e.name}` : `New ${cfg.kind} experiment`}</h2>
      <div className="row">
        <label>Name<input value={name} onChange={(ev) => setName(ev.target.value)} /></label>
        <label style={{ flex: 2 }}>Description<input value={description} onChange={(ev) => setDescription(ev.target.value)} /></label>
        {e && <label style={{ flexDirection: "row", gap: "0.3rem" }}><input type="checkbox" checked={active} onChange={(ev) => setActive(ev.target.checked)} />in use</label>}
      </div>
      {e && e.used_by.length > 0 && (
        <p className="small muted">Used by {e.used_by.map((p) => p.name).join(", ")}. Saving makes version {e.version + 1};
          those programs keep the version they have until you bring them up to date (offered after saving).</p>
      )}
      <ExperimentFields value={cfg} onChange={setCfg} />
      <details>
        <summary className="small">Every setting (JSON)</summary>
        <textarea rows={14} style={{ width: "100%", fontFamily: "monospace", fontSize: "0.8rem" }}
          value={json ?? JSON.stringify(cfg, null, 2)} onChange={(ev) => setJson(ev.target.value)} />
        {json !== null && (
          <button className="secondary" onClick={() => {
            try { setCfg(completeExperiment(JSON.parse(json) as ExperimentConfig)); setJson(null); setError(null); } catch (err) { setError(`JSON: ${errorText(err)}`); }
          }}>Use this JSON</button>
        )}
      </details>
      {error && <div className="error">{error}</div>}
      <div className="row" style={{ marginTop: "0.8rem" }}>
        <button disabled={!name.trim() || json !== null} onClick={() => void save()}>{e ? "Save (new version)" : "Create experiment"}</button>
        <button className="secondary" onClick={() => onDone(null)}>Cancel</button>
      </div>
    </div>
  );
}

/** After an edit: the programs still on an older version, to bring up to date. */
function UpdatePrograms({ e, onDone }: { e: Experiment; onDone: () => void }) {
  const old = e.used_by.filter((p) => p.versions.some((v) => v < e.version));
  const [pick, setPick] = useState<Set<number>>(new Set(old.map((p) => p.id)));
  const [error, setError] = useState<string | null>(null);
  const apply = async () => {
    try { await api.applyExperiment(e.id, [...pick]); onDone(); } catch (err) { setError(errorText(err)); }
  };
  return (
    <div className="card warn-card">
      <h2 style={{ marginTop: 0 }}>Bring programs up to version {e.version} of {e.name}?</h2>
      <p className="small">These programs still run an older version. Updating gives each a new version; sessions already
        made keep what they ran.</p>
      {old.map((p) => (
        <label key={p.id} style={{ flexDirection: "row", gap: "0.3rem" }}>
          <input type="checkbox" checked={pick.has(p.id)} onChange={(ev) => {
            const next = new Set(pick);
            if (ev.target.checked) next.add(p.id); else next.delete(p.id);
            setPick(next);
          }} />{p.name} (has version {p.versions.join(", ")})
        </label>
      ))}
      {error && <div className="error">{error}</div>}
      <div className="row" style={{ marginTop: "0.6rem" }}>
        <button disabled={pick.size === 0} onClick={() => void apply()}>Update {pick.size} program{pick.size === 1 ? "" : "s"}</button>
        <button className="secondary" onClick={onDone}>Leave them as they are</button>
      </div>
    </div>
  );
}

interface Pending { file: string; doc: unknown; check: ImportCheck | null; name: string; done: string | null; error: string | null }

/** Import experiment files (.json): each is checked first; nothing is saved until Import. */
function ImportCard({ onImported }: { onImported: () => void }) {
  const [items, setItems] = useState<Pending[]>([]);
  const update = (i: number, patch: Partial<Pending>) => setItems((xs) => xs.map((x, k) => (k === i ? { ...x, ...patch } : x)));
  const read = async (files: FileList | null) => {
    const next: Pending[] = [];
    for (const f of Array.from(files ?? [])) {
      let doc: unknown = null;
      let error: string | null = null;
      try { doc = JSON.parse(await f.text()); } catch { error = "not valid JSON"; }
      let check: ImportCheck | null = null;
      if (!error) {
        try { check = await api.checkExperimentImport(doc); } catch (err) { error = errorText(err); }
      }
      next.push({ file: f.name, doc, check, name: check?.name ?? "", done: null, error });
    }
    setItems(next);
  };
  const recheck = async (i: number) => {
    const it = items[i]!;
    try { update(i, { check: await api.checkExperimentImport(it.doc, it.name), error: null }); } catch (err) { update(i, { error: errorText(err) }); }
  };
  const doImport = async (i: number) => {
    const it = items[i]!;
    try {
      const e = await api.importExperiment(it.doc, it.name);
      update(i, { done: e.name, error: null });
      onImported();
    } catch (err) {
      update(i, { error: errorText(err) });
    }
  };
  return (
    <div className="card">
      <h2 style={{ marginTop: 0 }}>Import experiment files</h2>
      <p className="small muted">Files in the experiment format (<code>.experiment.json</code>; see docs/EXPERIMENT_FORMAT.md — another
        AI tool can write them from that guide and the schema). Each file is checked first: misspelt or unknown settings and
        values out of range are listed, and nothing is saved until you press Import.</p>
      <input type="file" accept=".json,application/json" multiple aria-label="Experiment files" onChange={(ev) => void read(ev.target.files)} />
      {items.map((it, i) => (
        <div key={i} className={`import-item ${it.check?.ok ? "" : "bad"}`}>
          <strong>{it.file}</strong>
          {it.error && <div className="error small">{it.error}</div>}
          {it.check && !it.check.ok && <ul className="small error">{it.check.errors.map((e) => <li key={e}>{e}</li>)}</ul>}
          {it.check?.ok && !it.done && (
            <>
              {it.check.warnings.length > 0 && <ul className="small warn">{it.check.warnings.map((w) => <li key={w}>{w}</li>)}</ul>}
              <div className="small">{it.check.document && experimentSummary(it.check.document.experiment)}</div>
              {it.check.same_settings_as && <div className="small warn">Same settings as “{it.check.same_settings_as.name}”.</div>}
              <div className="row" style={{ alignItems: "flex-end" }}>
                <label>Name<input value={it.name} onChange={(ev) => update(i, { name: ev.target.value })} onBlur={() => void recheck(i)} /></label>
                {it.check.name_taken && <span className="small error">This name is taken: change it to import.</span>}
                <button disabled={!!it.check.name_taken || !it.name.trim()} onClick={() => void doImport(i)}>Import</button>
              </div>
            </>
          )}
          {it.done && <div className="small good">Imported as “{it.done}”.</div>}
        </div>
      ))}
    </div>
  );
}
